import React, { useState, useMemo } from 'react';
import * as XLSX from 'xlsx';

const CXP3DataViewer = () => {
  const [formData, setFormData] = useState({
    fechaIni: '',
    fechaFin: ''
  });
  const [data, setData] = useState([]);
  const [filterLote, setFilterLote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    if (abortController) {
      abortController.abort();
    }
    
    const controller = new AbortController();
    setAbortController(controller);
    
    setLoading(true);
    setError(null);
    setData([]);
    setFilterLote('');
    setExpandedRows({});
    
    console.log('🔸 Fechas enviadas:', formData);

    const datos = {
      "Token": "LJKNFUH2FWEJBAEFFFFFKJ VGDEA003CSFCAQWEUIRRRRRRRRRRRADFYGBIFHVBESRNYHIOPWJROTY4WY",
      "Ambiente": "DESA",
      "Dispositivo": {
        "Marca": "SAMSUNG",
        "Modelo": "S22002B",
        "Version": "Android 18.125",
        "McAdress": null,
        "PowerStatus": "84%",
        "WifiStatus": "Conected to iphone de Mauricio",
        "GPSStatus": "ON",
        "GPS": "321|-485|321"
      },
      "Usr": {
        "Codigo": "",
        "UserNickName": "pierril"
      },
      "DatosMetodo": {
        "NombreMetodo": "DatosCajillasPorFecha",
        "Datos_Recibidos": JSON.stringify({
          "Fechaini": formData.fechaIni,
          "Fechafin": formData.fechaFin
        })
      }
    };

    try {
      const url = `https://appcorp.parma.com.gt/API/ReceptorDatos?Datos=${encodeURIComponent(JSON.stringify(datos))}`;
      
      const response = await fetch(url, { 
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });
      
      if (controller.signal.aborted) return;
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP: ${response.status} - ${errorText}`);
      }
      
      const apiData = await response.json(); 
      
      if (controller.signal.aborted) return;
      
      let datosReales = [];
      if (apiData && apiData.datos_Generados) {
        if (typeof apiData.datos_Generados === 'string') {
          try {
            datosReales = JSON.parse(apiData.datos_Generados);
          } catch (parseError) {
            throw new Error('Error al procesar los datos del servidor');
          }
        } else {
          datosReales = apiData.datos_Generados;
        }
      }
      
      setData(datosReales);
      
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(`Error: ${err.message}`);
      }
    } finally {
      if (!abortController?.signal.aborted) {
        setLoading(false);
      }
    }
  };

  const cancelRequest = () => {
    if (abortController) {
      abortController.abort();
      setLoading(false);
      setError('Consulta cancelada por el usuario');
    }
  };

  // Agrupar datos por Lote y Producto
  const datosAgrupadosPorLoteYProducto = useMemo(() => {
    const agrupados = {};
    
    data.forEach(item => {
      item.p?.forEach(grupo => {
        grupo.d?.forEach(cajilla => {
          const clave = `${cajilla.Lote}-${item.ItemCode}-${item.NombreProducto}`;
          
          if (!agrupados[clave]) {
            agrupados[clave] = {
              lote: cajilla.Lote,
              itemCode: item.ItemCode,
              producto: item.NombreProducto,
              fechaVencimiento: cajilla.FECHAVENCIMIENTO,
              registros: [],
              totalCajillas: 0,
              totalUnidades: 0,
              totalPesoBruto: 0,
              totalPesoNeto: 0,
              encargados: new Set(),
              fechas: new Set()
            };
          }
          
          // Agregar datos al grupo
          agrupados[clave].encargados.add(item.EncargadoPesaje);
          agrupados[clave].fechas.add(item.FechaCreacion.split('T')[0]);
          
          // Calcular totales
          const pesoNeto = cajilla.PESO - grupo.TaraCajilla;
          agrupados[clave].totalCajillas += 1;
          agrupados[clave].totalUnidades += cajilla.UNIDADES;
          agrupados[clave].totalPesoBruto += cajilla.PESO;
          agrupados[clave].totalPesoNeto += pesoNeto;
          
          // Guardar registro detallado
          agrupados[clave].registros.push({
            codigo: item.Codigo,
            fechaCreacion: item.FechaCreacion,
            horaCreacion: item.HoraCreacion,
            encargado: item.EncargadoPesaje,
            cantidadProducida: item.CE_CANTIDADPRODUCIDA,
            numCajilla: cajilla.NUMCAJILLA,
            unidades: cajilla.UNIDADES,
            pesoBruto: cajilla.PESO,
            tara: grupo.TaraCajilla,
            pesoNeto: pesoNeto,
            fechaVencimiento: cajilla.FECHAVENCIMIENTO
          });
        });
      });
    });
    
    // Convertir a array y ordenar
    return Object.values(agrupados)
      .map(grupo => ({
        ...grupo,
        encargados: Array.from(grupo.encargados),
        fechas: Array.from(grupo.fechas).sort(),
        promedioPesoNeto: grupo.totalPesoNeto / grupo.totalCajillas
      }))
      .sort((a, b) => a.lote.localeCompare(b.lote) || a.producto.localeCompare(b.producto));
  }, [data]);

  // Filtrar datos por Lote
  const filteredData = useMemo(() => {
    if (!filterLote) return datosAgrupadosPorLoteYProducto;
    return datosAgrupadosPorLoteYProducto.filter(item => item.lote === filterLote);
  }, [datosAgrupadosPorLoteYProducto, filterLote]);

  // Obtener todos los Lotes únicos para el filtro
  const lotesUnicos = useMemo(() => {
    return [...new Set(datosAgrupadosPorLoteYProducto.map(item => item.lote))].sort();
  }, [datosAgrupadosPorLoteYProducto]);

  // Función para exportar a Excel - DATOS ORIGINALES DEL API
  const exportToExcel = () => {
    if (data.length === 0) return;

    // Crear workbook
    const wb = XLSX.utils.book_new();

    // Hoja 1: Datos Originales del API (TAL CUAL VIENEN)
    const datosOriginales = data.flatMap(item => 
      item.p?.flatMap(grupo => 
        grupo.d?.map(cajilla => ({
          'Codigo': item.Codigo,
          'FechaCreacion': item.FechaCreacion,
          'HoraCreacion': item.HoraCreacion,
          'EncargadoPesaje': item.EncargadoPesaje,
          'ItemCode': item.ItemCode,
          'NombreProducto': item.NombreProducto,
          'CE_CANTIDADPRODUCIDA': item.CE_CANTIDADPRODUCIDA,
          'TaraCajilla': grupo.TaraCajilla,
          'Lote': cajilla.Lote,
          'FECHAVENCIMIENTO': cajilla.FECHAVENCIMIENTO,
          'NUMCAJILLA': cajilla.NUMCAJILLA,
          'UNIDADES': cajilla.UNIDADES,
          'PESO': cajilla.PESO,
          'PesoNeto': (cajilla.PESO - grupo.TaraCajilla).toFixed(2)
        })) || []
      ) || []
    );

    const wsOriginal = XLSX.utils.json_to_sheet(datosOriginales);
    XLSX.utils.book_append_sheet(wb, wsOriginal, "Datos Originales API");

    // Hoja 2: Resumen por Lote y Producto
    const resumenData = datosAgrupadosPorLoteYProducto.map(grupo => ({
      'Lote': grupo.lote,
      'ItemCode': grupo.itemCode,
      'Producto': grupo.producto,
      'Fecha Vencimiento': new Date(grupo.fechaVencimiento).toLocaleDateString(),
      'Total Cajillas': grupo.totalCajillas,
      'Total Unidades': grupo.totalUnidades,
      'Peso Bruto Total': grupo.totalPesoBruto.toFixed(2),
      'Peso Neto Total': grupo.totalPesoNeto.toFixed(2),
      'Peso Neto Promedio': grupo.promedioPesoNeto.toFixed(2),
      'Encargados': grupo.encargados.join(', '),
      'Fechas de Registro': grupo.fechas.join(', ')
    }));

    const wsResumen = XLSX.utils.json_to_sheet(resumenData);
    XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen Lote-Producto");

    // Hoja 3: Datos Filtrados (si hay filtro aplicado)
    if (filterLote) {
      const datosFiltrados = filteredData.flatMap(grupo =>
        grupo.registros.map(registro => ({
          'Lote': grupo.lote,
          'ItemCode': grupo.itemCode,
          'Producto': grupo.producto,
          'Fecha Vencimiento': new Date(registro.fechaVencimiento).toLocaleDateString(),
          'Código Registro': registro.codigo,
          'Número Cajilla': registro.numCajilla,
          'Unidades': registro.unidades,
          'Peso Bruto': registro.pesoBruto,
          'Tara': registro.tara,
          'Peso Neto': registro.pesoNeto.toFixed(2),
          'Fecha Creación': new Date(registro.fechaCreacion).toLocaleDateString(),
          'Hora Creación': registro.horaCreacion,
          'Encargado': registro.encargado,
          'Cantidad Producida': registro.cantidadProducida
        }))
      );

      const wsFiltrados = XLSX.utils.json_to_sheet(datosFiltrados);
      XLSX.utils.book_append_sheet(wb, wsFiltrados, `Filtrado Lote ${filterLote}`);
    }

    // Ajustar anchos de columnas
    const ajustarAnchos = (ws) => {
      const colWidths = [];
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let col = range.s.c; col <= range.e.c; col++) {
        let maxWidth = 0;
        for (let row = range.s.r; row <= range.e.r; row++) {
          const cell = ws[XLSX.utils.encode_cell({ r: row, c: col })];
          if (cell && cell.v) {
            const width = cell.v.toString().length;
            if (width > maxWidth) maxWidth = width;
          }
        }
        colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
      }
      ws['!cols'] = colWidths;
    };

    ajustarAnchos(wsOriginal);
    ajustarAnchos(wsResumen);
    if (filterLote) {
      const wsFiltrados = wb.Sheets[`Filtrado Lote ${filterLote}`];
      ajustarAnchos(wsFiltrados);
    }

    // Generar y descargar archivo
    const fileName = `datos_cajillas_${formData.fechaIni}_a_${formData.fechaFin}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const toggleRow = (clave) => {
    setExpandedRows(prev => ({
      ...prev,
      [clave]: !prev[clave]
    }));
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>📦 Datos por Lote y Producto - CXP3</h1>
      
      {/* Formulario de fechas */}
      <form onSubmit={handleSubmit} style={{ 
        marginBottom: '20px', 
        padding: '20px', 
        border: '1px solid #ccc',
        borderRadius: '5px',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', gap: '20px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Fecha Inicio:
            </label>
            <input
              type="date"
              name="fechaIni"
              value={formData.fechaIni}
              onChange={handleInputChange}
              required
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Fecha Fin:
            </label>
            <input
              type="date"
              name="fechaFin"
              value={formData.fechaFin}
              onChange={handleInputChange}
              required
              style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button 
            type="submit" 
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Consultando...' : 'Consultar Datos'}
          </button>
          
          {loading && (
            <button 
              type="button" 
              onClick={cancelRequest}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancelar
            </button>
          )}
        </div>
      </form>

      {/* Controles de filtro y exportación */}
      {datosAgrupadosPorLoteYProducto.length > 0 && (
        <div style={{ 
          marginBottom: '20px', 
          display: 'flex', 
          gap: '15px', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          padding: '15px',
          backgroundColor: '#e9ecef',
          borderRadius: '5px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label htmlFor="loteFilter" style={{ fontWeight: 'bold' }}>
              🔍 Filtrar por Lote:
            </label>
            <select
              id="loteFilter"
              value={filterLote}
              onChange={(e) => setFilterLote(e.target.value)}
              style={{ 
                padding: '8px', 
                minWidth: '200px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            >
              <option value="">Todos los Lotes</option>
              {lotesUnicos.map(lote => (
                <option key={lote} value={lote}>{lote}</option>
              ))}
            </select>
          </div>

          <button 
            onClick={exportToExcel}
            disabled={data.length === 0}
            style={{
              padding: '10px 20px',
              backgroundColor: data.length === 0 ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: data.length === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            📊 Exportar a Excel (.xlsx)
          </button>
        </div>
      )}

      {/* Mensajes de estado */}
      {loading && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          ⏳ Consultando datos del API...
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '15px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '15px'
        }}>
          ❌ <strong>Error:</strong> {error}
        </div>
      )}

      {/* Estadísticas */}
      {datosAgrupadosPorLoteYProducto.length > 0 && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#d1ecf1', 
          border: '1px solid #bee5eb',
          borderRadius: '4px'
        }}>
          <strong>📈 Estadísticas:</strong> 
          {' '}Total Grupos (Lote-Producto): <strong>{datosAgrupadosPorLoteYProducto.length}</strong> | 
          {' '}Filtrados: <strong>{filteredData.length}</strong> | 
          {' '}Total Cajillas: <strong>{datosAgrupadosPorLoteYProducto.reduce((sum, grupo) => sum + grupo.totalCajillas, 0)}</strong> |
          {' '}Lotes Únicos: <strong>{lotesUnicos.length}</strong>
          {filterLote && ` | Filtrado por Lote: <strong>${filterLote}</strong>`}
        </div>
      )}

      {/* Tabla principal - AGRUPADA POR LOTE Y PRODUCTO */}
      {filteredData.length > 0 ? (
        <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '5px' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'collapse',
            fontSize: '14px'
          }}>
            <thead>
              <tr style={{ backgroundColor: '#343a40', color: 'white' }}>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>#</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Lote</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Producto</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>ItemCode</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'left' }}>Vencimiento</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Cajillas</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Unidades</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Peso Neto Total</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Peso Neto Promedio</th>
                <th style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((grupo, index) => {
                const clave = `${grupo.lote}-${grupo.itemCode}-${grupo.producto}`;
                
                return (
                  <React.Fragment key={clave}>
                    <tr style={{ 
                      backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white'
                    }}>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6', fontWeight: 'bold' }}>
                        {index + 1}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        fontWeight: 'bold', 
                        color: '#e74c3c',
                        fontSize: '16px'
                      }}>
                        {grupo.lote}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6', fontWeight: 'bold' }}>
                        {grupo.producto}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        fontWeight: 'bold',
                        color: '#007bff'
                      }}>
                        {grupo.itemCode}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6' }}>
                        {new Date(grupo.fechaVencimiento).toLocaleDateString()}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        fontSize: '16px',
                        color: '#28a745'
                      }}>
                        {grupo.totalCajillas}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        textAlign: 'center', 
                        fontWeight: 'bold' 
                      }}>
                        {grupo.totalUnidades.toLocaleString()}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        color: '#dc3545'
                      }}>
                        {grupo.totalPesoNeto.toFixed(2)}
                      </td>
                      <td style={{ 
                        padding: '10px', 
                        border: '1px solid #dee2e6', 
                        textAlign: 'center', 
                        fontWeight: 'bold' 
                      }}>
                        {grupo.promedioPesoNeto.toFixed(2)}
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #dee2e6', textAlign: 'center' }}>
                        <button 
                          onClick={() => toggleRow(clave)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: expandedRows[clave] ? '#dc3545' : '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {expandedRows[clave] ? '📕 Ocultar' : '📖 Ver'} Detalles
                        </button>
                      </td>
                    </tr>
                    
                    {/* Fila expandida para detalles */}
                    {expandedRows[clave] && (
                      <tr>
                        <td colSpan="10" style={{ padding: '0', border: '1px solid #dee2e6' }}>
                          <div style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                              gap: '15px',
                              marginBottom: '20px'
                            }}>
                              <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                <strong>📋 Información del Grupo</strong>
                                <div>Lote: <strong>{grupo.lote}</strong></div>
                                <div>Producto: <strong>{grupo.producto}</strong></div>
                                <div>ItemCode: <strong>{grupo.itemCode}</strong></div>
                                <div>Vencimiento: <strong>{new Date(grupo.fechaVencimiento).toLocaleDateString()}</strong></div>
                              </div>
                              
                              <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                <strong>📊 Totales</strong>
                                <div>Cajillas: <strong>{grupo.totalCajillas}</strong></div>
                                <div>Unidades: <strong>{grupo.totalUnidades.toLocaleString()}</strong></div>
                                <div>Peso Neto Total: <strong>{grupo.totalPesoNeto.toFixed(2)}</strong></div>
                                <div>Peso Neto Promedio: <strong>{grupo.promedioPesoNeto.toFixed(2)}</strong></div>
                              </div>
                              
                              <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                <strong>👥 Encargados</strong>
                                {grupo.encargados.map((encargado, i) => (
                                  <div key={i}>• {encargado}</div>
                                ))}
                              </div>
                              
                              <div style={{ padding: '15px', backgroundColor: 'white', borderRadius: '5px', border: '1px solid #dee2e6' }}>
                                <strong>📅 Fechas de Registro</strong>
                                {grupo.fechas.map((fecha, i) => (
                                  <div key={i}>• {new Date(fecha).toLocaleDateString()}</div>
                                ))}
                              </div>
                            </div>

                            <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                              📦 Detalles de Cajillas
                            </h4>
                            
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse',
                                backgroundColor: 'white',
                                fontSize: '12px'
                              }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#6c757d', color: 'white' }}>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6' }}># Cajilla</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>Unidades</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>Peso Bruto</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>Tara</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>Peso Neto</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Fecha</th>
                                    <th style={{ padding: '8px', border: '1px solid #dee2e6' }}>Encargado</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {grupo.registros.map((registro, regIndex) => (
                                    <tr key={regIndex} style={{ 
                                      backgroundColor: regIndex % 2 === 0 ? '#f8f9fa' : 'white'
                                    }}>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6', fontWeight: 'bold' }}>
                                        {registro.numCajilla}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                                        {registro.unidades}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                                        {registro.pesoBruto}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right' }}>
                                        {registro.tara}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6', textAlign: 'right', fontWeight: 'bold' }}>
                                        {registro.pesoNeto.toFixed(2)}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                        {new Date(registro.fechaCreacion).toLocaleDateString()}
                                      </td>
                                      <td style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                                        {registro.encargado}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        datosAgrupadosPorLoteYProducto.length === 0 && !loading && (
          <div style={{ 
            padding: '40px', 
            textAlign: 'center', 
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            color: '#6c757d'
          }}>
            📅 Seleccione un rango de fechas y haga clic en "Consultar Datos" para cargar la información
          </div>
        )
      )}
    </div>
  );
};

export default CXP3DataViewer;